<p-toast></p-toast>
<p-confirmDialog></p-confirmDialog>

<!-- Toolbar: View Toggle -->
<div class="equipment-toolbar">
    @if (canCreateEquipment) {
        <button class="btn-primary" type="button" (click)="openNewEquipment()" [disabled]="isProjectInactive">
            <i class="ri-add-line"></i>
            <span>New Equipment</span>
        </button>
    }
    <div class="view-toggle-pill">
        <button class="toggle-btn" [class.active]="currentView === 'tile'" (click)="setView('tile')" title="Tile View">
            <i class="ri-layout-grid-line"></i>
        </button>
        <button class="toggle-btn" [class.active]="currentView === 'list'" (click)="setView('list')" title="List View">
            <i class="ri-menu-line"></i>
        </button>
    </div>
</div>

<!-- ═══ Tile View ═══ -->
@if (currentView === 'tile') {
    <div class="skid-grid">
        @if (filteredBlockScheduleList.length > 0) {
            @for (block of filteredBlockScheduleList; track $index) {
                <div class="skid-card">

                    <!-- Card Header -->
                    <div class="skid-card-header">
                        <div class="skid-title-group">
                            <span class="skid-title">{{ getSkidDisplayTitle(block) }}</span>
                            @if (block.integratorSkidSerialNo) {
                                <span class="skid-serial">{{ block.integratorSkidSerialNo }}</span>
                            }
                        </div>
                        @if (canEdit) {
                            <div class="skid-actions">
                                <button class="skid-edit-btn" (click)="openEditSkidName(block, $event)"
                                    [disabled]="isReadOnly" pTooltip="Edit Name" tooltipPosition="left">
                                    <i class="ri-edit-line"></i>
                                </button>
                                @if (!block.isCustom) {
                                <button class="skid-edit-btn" (click)="updateSkidInfo(block, $event)"
                                    [disabled]="isReadOnly" pTooltip="View" tooltipPosition="left">
                                    <i class="ri-eye-line"></i>
                                </button>
                                }
                            </div>
                        }
                    </div>

                    <!-- Template Rows -->
                    <div class="skid-card-body">
                        @for (item of getTemplatesForSkid(block.blockScheduleId!); track item.templateId; let last = $last) {
                            <div
                                class="template-row"
                                [class.template-row--locked]="isTemplateLocked(block.blockScheduleId!, item.templateId!)"
                                [class.template-row--readonly]="getTemplateAccessState(block.blockScheduleId!, item.templateId!) === 'readonly'"
                                (click)="openSkidChecksheet(block, item)"
                                [pTooltip]="getTemplateLockedMessage(block.blockScheduleId!, item.templateId!)"
                                tooltipPosition="top">
                                    <div class="template-row-header">
                                    <div class="template-title-wrap">
                                        <span class="template-name">{{ item?.shortCode || item?.templateName }}</span>
                                        @if (getOpenTaskCount(block.blockScheduleId!, item.templateId!) > 0) {
                                            <span class="open-task-badge">
                                                {{ getOpenTaskCount(block.blockScheduleId!, item.templateId!) > 99 ? '99+' : getOpenTaskCount(block.blockScheduleId!, item.templateId!) }}
                                            </span>
                                        }
                                    </div>
                                    <div class="flex align-items-center gap-2">
                                        @if (isTemplateLocked(block.blockScheduleId!, item.templateId!)) {
                                            <span class="template-lock" aria-label="Locked">
                                                <i class="ri-lock-2-line"></i>
                                            </span>
                                        }
                                        <p-tag
                                            [value]="getStatusLabel(getTemplateStatus(block.blockScheduleId!, item.templateId!).status)"
                                            [severity]="getStatusSeverity(getTemplateStatus(block.blockScheduleId!, item.templateId!).status)"
                                            class="status-tag">
                                        </p-tag>
                                        @if (canDeleteTemplate) {
                                            <button
                                                class="action-icon-button delete"
                                                type="button"
                                                [disabled]="isReadOnly"
                                                (click)="removeTemplateFromSkid(block.blockScheduleId!, item.templateId!, $event)"
                                                pTooltip="Remove template from this MV Power Station"
                                                tooltipPosition="left">
                                                <i class="ri-delete-bin-line"></i>
                                            </button>
                                        }
                                    </div>
                                </div>
                                <div class="template-row-dates">
                                    <div class="date-row">
                                        <span class="date-label">Date</span>
                                        <span class="date-value">{{ getTemplateStatus(block.blockScheduleId!, item.templateId!).date | date:'MMM dd, yyyy' }}</span>
                                    </div>
                                    <div class="date-row">
                                        <span class="date-label">Due Date</span>
                                        <span class="date-value">{{ getTemplateStatus(block.blockScheduleId!, item.templateId!).duedate | date:'MMM dd, yyyy' }}</span>
                                    </div>
                                </div>
                            </div>
                            @if (!last) {
                                <div class="template-divider"></div>
                            }
                        }
                    </div>

                </div>
            }
        } @else {
            <div class="empty-state">
                <i class="pi pi-search empty-icon"></i>
                <p class="empty-title">
                    @if (searchText) {
                        No MV Power Stations found matching "{{ searchText }}"
                    } @else {
                        No Block Schedule Available
                    }
                </p>
                <p class="empty-subtitle">
                    @if (searchText) {
                        Try adjusting your search criteria
                    } @else {
                        Block schedule data will appear here once loaded.
                    }
                </p>
            </div>
        }
    </div>
}

<!-- ═══ List View ═══ -->
@if (currentView === 'list') {
    <div class="eq-list-wrap">
        @if (filteredBlockScheduleList.length > 0) {
            <div class="eq-list">
                @for (block of filteredBlockScheduleList; track block.blockScheduleId) {
                    <div class="eq-list-row">

                        <!-- Left: Skid Identity -->
                        <div class="eq-list-skid">
                            <div class="eq-skid-line">
                                <i class="ri-hashtag"></i>
                                <span class="eq-skid-value">{{ getSkidDisplayTitle(block) }}</span>
                            </div>
                            @if (block.integratorSkidSerialNo) {
                                <div class="eq-skid-line eq-skid-line--subtle">
                                    <span class="eq-skid-subvalue">{{ block.integratorSkidSerialNo }}</span>
                                </div>
                            }
                        </div>

                        <!-- Middle: Template Short Code Pills -->
                        <div class="eq-list-templates">
                            @for (tmpl of getTemplatesForSkid(block.blockScheduleId!); track tmpl.templateId; let last = $last) {
                                <button
                                    class="eq-tmpl-pill eq-tmpl-pill--{{ getStatusClass(getTemplateStatus(block.blockScheduleId!, tmpl.templateId!).status) }}"
                                    [class.eq-tmpl-pill--locked]="isTemplateLocked(block.blockScheduleId!, tmpl.templateId!)"
                                    (click)="openSkidChecksheet(block, tmpl)"
                                    [pTooltip]="getTemplateLockedMessage(block.blockScheduleId!, tmpl.templateId!) || tmpl.templateName"
                                    tooltipPosition="top"
                                    [disabled]="isTemplateLocked(block.blockScheduleId!, tmpl.templateId!)">
                                    @if (isTemplateLocked(block.blockScheduleId!, tmpl.templateId!)) {
                                        <i class="ri-lock-2-line"></i>
                                    }
                                    {{ tmpl.shortCode || (tmpl.templateName | slice:0:5) }}
                                    @if (getOpenTaskCount(block.blockScheduleId!, tmpl.templateId!) > 0) {
                                        <span class="open-task-badge">
                                            {{ getOpenTaskCount(block.blockScheduleId!, tmpl.templateId!) > 99 ? '99+' : getOpenTaskCount(block.blockScheduleId!, tmpl.templateId!) }}
                                        </span>
                                    }
                                </button>
                                @if (!last) {
                                    <span class="eq-tmpl-connector"></span>
                                }
                            }
                        </div>

                        <!-- Action -->
                        @if (canEdit) {
                            <div class="eq-list-actions">
                                <button class="eq-list-action-btn" (click)="openEditSkidName(block, $event)"
                                    [disabled]="isReadOnly" pTooltip="Edit Name" tooltipPosition="left">
                                    <i class="ri-edit-line"></i>
                                </button>
                                @if (!block.isCustom) {
                                <button class="eq-list-action-btn" (click)="updateSkidInfo(block, $event)"
                                    [disabled]="isReadOnly" pTooltip="View" tooltipPosition="left">
                                    <i class="ri-eye-line"></i>
                                </button>
                                }
                            </div>
                        }

                    </div>
                }
            </div>
        } @else {
            <div class="empty-state">
                <i class="pi pi-search empty-icon"></i>
                <p class="empty-title">
                    @if (searchText) {
                        No MV Power Stations found matching "{{ searchText }}"
                    } @else {
                        No Block Schedule Available
                    }
                </p>
                <p class="empty-subtitle">
                    @if (searchText) {
                        Try adjusting your search criteria
                    } @else {
                        Block schedule data will appear here once loaded.
                    }
                </p>
            </div>
        }
    </div>
}

<p-dialog
    header="Edit MV Power Station Name"
    [(visible)]="skidNameDialogVisible"
    [modal]="true"
    styleClass="kansei-dialog"
    [closable]="!skidNameSaving"
    [dismissableMask]="!skidNameSaving"
    [style]="{ width: '24rem' }"
    [contentStyle]="{ padding: '0px' }"
    (onHide)="closeEditSkidNameDialog()">

    <div class="modal-form-body">
        <div class="grid">
            <div class="col-12">
                <label for="skidNameInput" class="modal-label">Name</label>
                <input
                    id="skidNameInput"
                    type="text"
                    pInputText
                    [(ngModel)]="skidNameInput"
                    maxlength="50"
                    class="w-full p-inputtext-lg" />
                <div class="skid-name-count">{{ (skidNameInput || '').length }} / 50</div>
            </div>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <div class="modal-footer" style="margin: 10px -16px -12px; border-radius: 0;">
            <button type="button" class="modal-btn-cancel" [disabled]="skidNameSaving" (click)="closeEditSkidNameDialog()">
                Cancel
            </button>
            <button type="button" class="modal-btn-primary" [disabled]="skidNameSaving" (click)="saveSkidName()">
                {{ skidNameSaving ? 'Saving...' : 'Save Changes' }}
            </button>
        </div>
    </ng-template>
</p-dialog>

<p-dialog
    header="Assign Templates to MV Power Stations"
    [(visible)]="assignTemplatesDialogVisible"
    [modal]="true"
    styleClass="kansei-dialog"
    [style]="{ width: '34rem' }"
    [closable]="!assigningTemplates"
    [dismissableMask]="!assigningTemplates"
    (onHide)="closeAssignTemplatesDialog()">

    <div class="modal-form-body">
        <div class="grid">
            <div class="col-12">
                <label class="modal-label">Templates</label>
                <p-multiSelect
                    [options]="getTemplateAssignOptions()"
                    [(ngModel)]="selectedAssignTemplateIds"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select templates"
                    [showToggleAll]="true"
                    [filter]="true"
                    filterBy="label"
                    [disabled]="assigningTemplates"
                    appendTo="body"
                    styleClass="w-full">
                </p-multiSelect>
            </div>
            <div class="col-12">
                <label class="modal-label">MV Power Stations</label>
                <p-multiSelect
                    [options]="getSkidAssignOptions()"
                    [(ngModel)]="selectedAssignSkidIds"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select MV Power Stations"
                    [showToggleAll]="true"
                    [filter]="true"
                    filterBy="label"
                    [disabled]="assigningTemplates"
                    appendTo="body"
                    styleClass="w-full">
                </p-multiSelect>
            </div>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <div class="modal-footer" style="margin: 10px -16px -12px; border-radius: 0;">
            <button type="button" class="modal-btn-cancel" [disabled]="assigningTemplates" (click)="closeAssignTemplatesDialog()">
                Cancel
            </button>
            <button
                type="button"
                class="modal-btn-primary"
                [disabled]="assigningTemplates || selectedAssignTemplateIds.length === 0 || selectedAssignSkidIds.length === 0"
                (click)="saveAssignTemplatesToSkids()">
                {{ assigningTemplates ? 'Assigning...' : 'Assign' }}
            </button>
        </div>
    </ng-template>
</p-dialog>
